import { useState } from 'react';
import { Modal, Typography, Space, Tag, Alert, App as AntApp } from 'antd';
import { DappIdentityNotice } from './DappIdentityNotice';
import { TypedDataSummary } from './TypedDataSummary';
import { useArmDelay } from '@/hooks/useArmDelay';
import { dappIdentityAllowsApproval } from '@/lib/walletconnect/verify';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useWalletConnect } from '@/hooks/useWalletConnect';
import { useVaultMessageSigning } from '@/hooks/useVaultMessageSigning';
import { useAppSelector } from '@/store/hooks';
import { chainMetaByKey } from '@/lib/walletconnect/chains';
import { buildSignatureSigningParams } from '@/lib/walletconnect/signatureSigning';
import { vaultsApi } from '@/api/vaults';
import type { SignatureRequest } from '@/types/vault';

const { Text, Paragraph } = Typography;

/**
 * Shown when a connected dApp requests a message signature (personal_sign / SIWE /
 * Snapshot). The user reviews the message; on approval it becomes an M-of-N vault
 * signature request. The creator co-signs inline; for multi-signer vaults the
 * remaining designated signers co-sign it from the signature-request page.
 */
export function DappSignRequestModal() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const navigate = useNavigate();
  const wc = useWalletConnect();
  const { signMessage, getLastError } = useVaultMessageSigning();
  const wkIdentity = useAppSelector((state) => state.auth.user?.wkIdentity);
  const currentOrg = useAppSelector((state) => state.organization.currentOrg);
  const organizations = useAppSelector((state) => state.organization.organizations);
  const [submitting, setSubmitting] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const req = wc?.pendingSignRequest ?? null;
  const open = !!req;
  const chainMeta = req ? chainMetaByKey(req.binding.chain) : undefined;
  const requestKey = req ? `${req.request.topic}:${String(req.request.id)}` : null;
  const armed = useArmDelay(requestKey);
  const isTypedData = req?.messageType === 'eth_signTypedData_v4';
  const canApprove =
    !!req &&
    armed &&
    dappIdentityAllowsApproval(req.identity, acknowledged) &&
    // Typed data must decode, must not be bound to another chain, and a
    // permission it carries must be fully decoded (fail closed).
    (!isTypedData ||
      (!!req.typedData && req.typedData.undecodable === null && req.typedDataChain !== 'mismatch'));

  /**
   * Resolve the exact-M designated signers an M>1 vault needs. Auto-selects the
   * creator first (if they can sign) then the remaining signers in canonical
   * order — enough to unblock multi-sig message signing without a picker.
   */
  const resolveDesignatedSigners = async (
    orgId: string,
    vaultId: string
  ): Promise<string[] | undefined> => {
    const vault = await vaultsApi.getVault(orgId, vaultId);
    const M = vault.requiredSigners;
    if (M <= 1) return undefined; // backend auto-designates the creator
    const members = await vaultsApi.getVaultMembers(orgId, vaultId);
    const eligible = members
      .filter(
        (m) =>
          (m.role === 'signer' || m.role === 'admin') &&
          m.xpubStatus === 'submitted' &&
          !m.removedAt
      )
      .map((m) => m.wkIdentity);
    if (eligible.length < M) {
      throw new Error(t('walletconnect.sign.not_enough_signers', { count: M }));
    }
    const ordered = [...eligible].sort();
    const picked: string[] = [];
    if (wkIdentity && ordered.includes(wkIdentity)) picked.push(wkIdentity);
    for (const wk of ordered) {
      if (picked.length >= M) break;
      if (!picked.includes(wk)) picked.push(wk);
    }
    return picked.slice(0, M).sort();
  };

  const handleApprove = async () => {
    if (!req || !wc || !canApprove) return;
    if (!wkIdentity) {
      void message.error(t('walletconnect.errors.not_initialized'));
      return;
    }
    // The bound vault's org (currentOrg is only loaded by the settings page):
    // its index is part of the signing path, so never default it to 0.
    const bindingOrg =
      currentOrg?.id === req.binding.orgId
        ? currentOrg
        : organizations.find((o) => o.id === req.binding.orgId);
    if (!bindingOrg) {
      void message.error(t('errors.vaultDescriptorRequestMismatch'));
      return;
    }
    setSubmitting(true);
    try {
      const designatedSigners = await resolveDesignatedSigners(
        req.binding.orgId,
        req.binding.vaultId
      );

      const sigReq: SignatureRequest = await vaultsApi.createSignatureRequest(
        req.binding.orgId,
        req.binding.vaultId,
        {
          message: req.message,
          messageType: req.messageType,
          // Verify-attested origin, never the self-declared name.
          dappOrigin: wc.recordLabel(req.identity),
          sourceAddressIndex: req.binding.addressIndex,
          ...(designatedSigners ? { designatedSigners } : {}),
        }
      );

      // Hold the dApp request open — the signature poll resolves it once signed.
      wc.registerPendingSignature({
        topic: req.request.topic,
        requestId: req.request.id,
        signatureRequestId: sigReq.id,
        orgId: req.binding.orgId,
        vaultId: req.binding.vaultId,
      });
      wc.clearSignRequest();

      // If the caller is a designated signer, co-sign now (completes M=1 inline;
      // records the creator's contribution for M>1).
      if (sigReq.designatedSigners.includes(wkIdentity)) {
        const params = buildSignatureSigningParams(sigReq, wkIdentity);
        const result = await signMessage({
          orgId: req.binding.orgId,
          vaultId: req.binding.vaultId,
          chain: sigReq.chain,
          orgIndex: bindingOrg.orgIndex,
          vaultIndex: sigReq.vaultIndex ?? 0,
          addressIndex: sigReq.sourceAddressIndex,
          vaultName: req.binding.vaultName,
          orgName: bindingOrg.name,
          digest: sigReq.digest,
          displayMessage: sigReq.displayMessage,
          dappOrigin: wc.recordLabel(req.identity),
          signingMode: sigReq.signingMode,
          ...params,
        });
        if (!result) {
          // The request exists but this signer's contribution failed (or the
          // device refused it): say why and open the request so it can be
          // retried from there.
          void message.error(getLastError() ?? t('walletconnect.errors.sign_failed'));
          void navigate(
            `/vaults/${req.binding.vaultId}/signature-requests/${sigReq.id}?org=${req.binding.orgId}`
          );
          return;
        }
        await vaultsApi.signSignatureRequest(req.binding.orgId, req.binding.vaultId, sigReq.id, {
          walletSignatures: result.walletSignatures,
          keySignatures: result.keySignatures,
          walletPubKey: result.walletPubKey,
          keyPubKey: result.keyPubKey,
        });
      }

      if (sigReq.requiredSignatures > 1) {
        void message.success(t('walletconnect.sign.submitted_multi'));
        void navigate(
          `/vaults/${req.binding.vaultId}/signature-requests/${sigReq.id}?org=${req.binding.orgId}`
        );
      } else {
        void message.success(t('walletconnect.sign.submitted'));
      }
    } catch (err) {
      const code = (err as Error & { errorCode?: string }).errorCode;
      const msg =
        code === 'ACCOUNT_NOT_DEPLOYED'
          ? t('walletconnect.sign.not_deployed')
          : err instanceof Error
            ? err.message
            : t('walletconnect.errors.sign_failed');
      void message.error(msg);
      await wc.rejectSignRequest();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('walletconnect.sign.title')}
      okText={t('walletconnect.sign.approve')}
      cancelText={t('walletconnect.sign.reject')}
      onOk={() => void handleApprove()}
      onCancel={() => void wc?.rejectSignRequest()}
      confirmLoading={submitting}
      okButtonProps={{ disabled: !canApprove }}
      destroyOnHidden
      width={560}
    >
      {req && (
        <Space
          key={requestKey ?? undefined}
          orientation="vertical"
          size="middle"
          style={{ width: '100%' }}
        >
          {(wc?.signQueueSize ?? 0) > 1 && (
            <Alert
              type="info"
              showIcon
              title={t('walletconnect.verify.queue_position', { count: wc?.signQueueSize ?? 0 })}
            />
          )}
          <DappIdentityNotice identity={req.identity} onAcknowledgedChange={setAcknowledged} />
          <div>
            <Text type="secondary">{t('walletconnect.sign.vault')}: </Text>
            <Text strong>{req.binding.vaultName}</Text> {chainMeta && <Tag>{chainMeta.name}</Tag>}
          </div>
          {isTypedData ? (
            <TypedDataSummary
              decoded={req.typedData ?? null}
              chainCheck={req.typedDataChain ?? null}
              vaultChainId={chainMeta?.chainId}
              rawJson={req.message}
            />
          ) : (
            <div>
              <Text type="secondary">{t('walletconnect.sign.message')}:</Text>
              <Paragraph
                code
                style={{ marginTop: 6, maxHeight: 200, overflow: 'auto', whiteSpace: 'pre-wrap' }}
              >
                {req.message}
              </Paragraph>
            </div>
          )}
          <Alert type="info" showIcon title={t('walletconnect.sign.note')} />
        </Space>
      )}
    </Modal>
  );
}
